import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';

/* ═══════════════════════════════════════════════════════════
   VideoBackground — fixed full-viewport video behind content.
   Reads the video URL + overlay opacity from ThemeContext.
   ═══════════════════════════════════════════════════════════ */

export default function VideoBackground() {
  const { backgroundVideo } = useTheme();
  const videoRef = useRef(null);
  const [error, setError] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');

  const url     = backgroundVideo?.url || '';
  const opacity = backgroundVideo?.overlayOpacity ?? 0.7;

  // Reset error when URL changes
  useEffect(() => { setError(false); setErrorDetail(''); }, [url]);

  // Ensure autoplay regardless of browser quirks
  useEffect(() => {
    if (videoRef.current && url && !error) {
      videoRef.current.play().catch((err) => {
        console.warn('[VideoBackground] Autoplay blocked:', err.message);
      });
    }
  }, [url, error]);

  if (!url) return null;

  // Silently hide the video if it fails to load (no user-facing error)
  if (error) return null;

  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Video element */}
      <video
        ref={videoRef}
        key={url}
        className="absolute inset-0 w-full h-full object-cover"
        src={url}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        onError={(e) => {
          const mediaErr = e.target?.error;
          const detail = mediaErr
            ? `Error ${mediaErr.code}: ${mediaErr.message || ['', 'MEDIA_ERR_ABORTED', 'MEDIA_ERR_NETWORK', 'MEDIA_ERR_DECODE', 'MEDIA_ERR_SRC_NOT_SUPPORTED'][mediaErr.code] || 'Unknown'}`
            : 'Failed to load video';
          console.error('[VideoBackground]', detail, url);
          setErrorDetail(detail);
          setError(true);
        }}
      />
      {/* Tinted overlay — uses the current theme body colour for a blended look */}
      <div
        className="absolute inset-0 bg-backgroundBody"
        style={{ opacity }}
      />
    </div>
  );
}
